import * as C from "@sp-crm/core";
import {
    AsyncOperationId,
    AsyncOperationStatus,
    InternalId,
    isAsyncOperationTerminal,
} from "@sp-crm/core";
import { trpcClient } from "components/rpc/trpc";
import { QueryClient } from "react-query";
import { Action, Dispatch } from "redux";
import { handleEmailSent } from "../../util/email-sent";
import * as http from "../../util/http";
import { Actions } from "./index";
import { makeRequestAction } from "./types";

export interface ClientIntakeDeliveryResult {
    accepted: string[];
    rejected: string[];
    code?: string;
}

export interface ClientIntakeEmailResponse {
    deliveryResults: ClientIntakeDeliveryResult[];
    canceled?: boolean;
    errorMessage?: string | null;
}

export interface ClientIntakeSendResult extends ClientIntakeEmailResponse {
    anyAccepted: boolean;
}

export const readSendOperationId = (data: unknown): AsyncOperationId | null => {
    const started = data as { operationId?: AsyncOperationId } | undefined;
    return started?.operationId ?? null;
};

const pollIntervalMs = 1000;
const maxPollWaitMs = 15 * 60 * 1000;
const maxPollAttempts = maxPollWaitMs / pollIntervalMs;
const maxConsecutivePollFailures = 5;

const delay = (ms: number): Promise<void> =>
    new Promise(resolve => setTimeout(resolve, ms));

export interface SendSettlementPoller {
    getStatus(operationId: AsyncOperationId): Promise<AsyncOperationStatus>;
    wait(ms: number): Promise<void>;
    onSettled(): void;
}

export const pollUntilSendSettles = async (
    operationId: AsyncOperationId,
    poller: SendSettlementPoller,
): Promise<void> => {
    let consecutiveFailures = 0;
    for (let attempt = 0; attempt < maxPollAttempts; attempt++) {
        try {
            const status = await poller.getStatus(operationId);
            consecutiveFailures = 0;
            if (isAsyncOperationTerminal(status)) {
                break;
            }
        } catch {
            consecutiveFailures++;
            if (consecutiveFailures >= maxConsecutivePollFailures) {
                break;
            }
        }
        await poller.wait(pollIntervalMs);
    }
    poller.onSettled();
};

export const refreshFeedWhenSendSettles = (
    operationId: AsyncOperationId,
    queryClient: QueryClient,
): Promise<void> =>
    pollUntilSendSettles(operationId, {
        getStatus: async id => (await trpcClient.asyncOperation.get.query({ id })).status,
        wait: delay,
        onSettled: () => handleEmailSent(queryClient),
    });

export async function sendClientIntakeEmail(
    eventId: InternalId,
    messageDetails: C.ClientIntakeMessage,
    dispatch: Dispatch<Action>,
): Promise<void> {
    dispatch(makeRequestAction(Actions.EMAIL_FORM_SUBMIT, eventId));
    const requestDescription = http.newRequestDescription(
        "/api/trpc/clientIntakeNotifications.start",
    );

    try {
        const { operationId } = await trpcClient.clientIntakeNotifications.start.mutate({
            payload: messageDetails,
        });
        dispatch(
            makeRequestAction(Actions.EMAIL_FORM_OPERATION_STARTED, eventId, {
                operationId,
            }),
        );
    } catch (e) {
        http.errorHandler(e, requestDescription, dispatch);
        dispatch(
            makeRequestAction(Actions.EMAIL_FORM_ERROR, eventId, { deliveryResults: [] }),
        );
    }
}
